import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { formatPrice } from '@/utils/formatter';
import {
  ProductItemContainer,
  ProductItem,
  ProductImageContainer,
  ProductInfoContainer,
  ProductInfo,
  ProductName,
  Row,
  IconButton,
  ProductDescription,
  ProductPrice,
  ProductImage,
} from './styled';
import ProductOrderSheet from './ProductOrderSheet/index';
import ProductEditModal from './ProductOrderSheet/ProductEditModal';

import { removeProductFromCategory as removeProductFromCategoryMutation } from '../../../../graphql/mutations';
import HiddenReusableIndicator from '../../../../components/HiddenReusableIndicator';
import {
  brandCategoryProductsByStore as brandCategoryProductsByStoreQuery,
  productCategories as productCategoriesMutation,
} from '../../../../graphql/queries';

function ProductListItem({
  product,
  category,
  selected,
  onClick,
  tabBarRef,
  productCategoryMap,
  allExtrasForBrand,
  storeId,
  brandId,
  vatRates,
}) {
  const productRowRef = useRef();
  const [isEditModalOpen, toggleEditModal] = useState(false);
  const [removeProductFromCategory] = useMutation(
    removeProductFromCategoryMutation,
  );

  useEffect(() => {
    requestAnimationFrame(() => {
      if (selected && productRowRef.current && tabBarRef.current) {
        const top =
          productRowRef.current.offsetTop - tabBarRef.current.clientHeight;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    });
  }, [selected, tabBarRef]);

  function handleClick() {
    onClick(product);
  }

  function openEditModal(e) {
    e.stopPropagation();
    toggleEditModal(true);
  }

  function closeEditModal() {
    toggleEditModal(false);
  }

  function onRemoveProductFromCategoryClick(e) {
    e.stopPropagation();
    const productIdToRemove = product.id;
    const categoryId = category.id;

    const removable = window.confirm(
      `Are you sure you want to remove the product "${product.name}" from the category?`,
    );
    if (removable) {
      removeProductFromCategory({
        variables: {
          product_id: productIdToRemove,
          category_id: categoryId,
        },
        refetchQueries: [
          {
            query: brandCategoryProductsByStoreQuery,
            variables: {
              store_id: storeId,
            },
          },
          {
            query: productCategoriesMutation,
            variables: {
              id: storeId,
              filter: 'ALL',
            },
          },
        ],
        update: (apolloStore, mutationResult) => {
          //if (mutationResult.data.removeProductFromCategory) {
          //  removeProductFromApolloStore(apolloStore, storeId, productIdToRemove, categoryId)
          //}
        },
        onError: (err) => {
          // eslint-disable-next-line no-console
          console.error('handleRemoveFromCategory', err);
          // eslint-disable-next-line no-alert
          alert(err.reason ? err.reason : err);
        },
      });
    }
  }

  return (
    <React.Fragment>
      <ProductItemContainer
        ref={productRowRef}
        key={product.id}
        onClick={handleClick}
      >
        <ProductItem>
          <ProductImageContainer>
            <ProductImage
              src={
                product.image ||
                category.icon ||
                'https://content.pickpack.de/width/700/foil1/_products_/placeholder/shoppingbag_v2.png'
              }
            />
          </ProductImageContainer>
          <ProductInfoContainer>
            <ProductInfo>
              <Row>
                <ProductName title={product.id}>
                  {product.name}{' '}
                  <HiddenReusableIndicator
                    published={product.published}
                    reusable_packaging={product.reusable_packaging}
                  />
                </ProductName>
                <IconButton onClick={openEditModal} name="edit" />
                <IconButton
                  onClick={onRemoveProductFromCategoryClick}
                  name="trash"
                  color="red"
                />
              </Row>
              <ProductDescription>{product.teaser_text}</ProductDescription>
            </ProductInfo>
            <ProductPrice discounted={!!product.old_price || product.is_promo}>
              {formatPrice(product.price)}
            </ProductPrice>
          </ProductInfoContainer>
        </ProductItem>
      </ProductItemContainer>
      {/* Drawer */}
      {selected && (
        <ProductOrderSheet
          brandId={brandId}
          storeId={storeId}
          product={product}
          allExtrasForBrand={allExtrasForBrand}
          productCategoryMap={productCategoryMap}
          vatRates={vatRates}
        />
      )}
      {isEditModalOpen && (
        <ProductEditModal
          storeId={storeId}
          product={product}
          category={category}
          onClose={closeEditModal}
          productCategoryMap={productCategoryMap}
          vatRates={vatRates}
        />
      )}
    </React.Fragment>
  );
}

ProductListItem.propTypes = {
  product: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  tabBarRef: PropTypes.object.isRequired,
  productCategoryMap: PropTypes.object,
  category: PropTypes.object.isRequired,
  storeId: PropTypes.string.isRequired,
  brandId: PropTypes.string.isRequired,
  allExtrasForBrand: PropTypes.arrayOf(PropTypes.object).isRequired,
  vatRates: PropTypes.arrayOf(PropTypes.number).isRequired,
};

ProductListItem.defaultProps = {
  selected: false,
  productCategoryMap: {},
};

export default ProductListItem;
